import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import {
    removeFromOpportunityLists,
    removeWebsitesFromListOpportunities,
    updateOpportunityLists,
} from "../helpers/helpers";
import type { OpportunitiesState } from "../types";

import * as actionCreators from "./action-creators";

const INITIAL_OPPORTUNITIES_STATE: OpportunitiesState = {
    opportunityLists: [],
    opportunityListsFetching: false,
    opportunityListsFetchError: undefined,
    opportunityListCreating: false,
    opportunityListCreateError: undefined,
    opportunityListUpdating: false,
    opportunityListUpdateError: undefined,
    opportunityListDeleting: false,
    opportunityListDeleteError: undefined,
    opportunityListModal: {
        isOpen: false,
        list: undefined,
        searchResults: [],
        searching: false,
        searchError: undefined,
    },
    isListDeleteModalOpen: false,
    isListRenameModalOpen: false,
    excludeListsDuplicateOpportunities: undefined,
    excludeListsDuplicateOpportunitiesFetching: false,
    excludeListsDuplicateOpportunitiesError: undefined,
    isExcludeListsDuplicateOpportunitiesModalOpen: false,
    isRowSelectionEnabled: false,
    tableExcelDownloading: false,
    recommendations: [],
    recommendationsFetching: false,
    recommendationsBarOpen: false,
    opportunityListName: "",
    opportunityListId: "",
    notRelevantResultsModalPayload: undefined,
    opportunityListIdToShowUpdatedListModal: undefined,
    shouldWaitToFiltersFromSavedSearch: false,
};

const opportunitiesReducer = createReducer<OpportunitiesState, ActionType<typeof actionCreators>>(
    INITIAL_OPPORTUNITIES_STATE,
)
    .handleAction(actionCreators.fetchOpportunityListsAsync.success, (state, { payload }) => ({
        ...state,
        opportunityLists: payload,
        opportunityListsFetching: false,
    }))
    .handleAction(
        actionCreators.listDeleteModalOpenSetAction,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            isListDeleteModalOpen: payload,
        }),
    )
    .handleAction(
        actionCreators.listRenameModalOpenSetAction,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            isListRenameModalOpen: payload,
        }),
    )
    .handleAction(
        actionCreators.createOpportunityListAsync.request,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListCreating: true,
            opportunityListCreateError: undefined,
        }),
    )
    .handleAction(
        actionCreators.createOpportunityListAsync.failure,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListCreating: false,
            opportunityListCreateError: payload,
        }),
    )
    .handleAction(
        actionCreators.createOpportunityListAsync.success,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListCreating: false,
            opportunityLists: [payload, ...state.opportunityLists],
        }),
    )
    .handleAction(
        actionCreators.updateOpportunityListAsync.request,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: true,
            opportunityListUpdateError: undefined,
        }),
    )
    .handleAction(
        actionCreators.updateOpportunityListAsync.failure,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: false,
            opportunityListUpdateError: payload,
        }),
    )
    .handleAction(
        actionCreators.updateOpportunityListAsync.success,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: false,
            opportunityLists: updateOpportunityLists(state.opportunityLists, payload),
        }),
    )
    .handleAction(
        actionCreators.updateOpportunityList,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityLists: updateOpportunityLists(state.opportunityLists, payload),
        }),
    )
    .handleAction(
        actionCreators.updateListOpportunitiesAsync.request,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: true,
            opportunityListUpdateError: undefined,
        }),
    )
    .handleAction(
        actionCreators.updateListOpportunitiesAsync.success,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: false,
            opportunityLists: state.opportunityLists.map((list) => {
                if (list.opportunityListId !== payload.opportunityListId) {
                    return list;
                }

                return {
                    ...list,
                    opportunities: [...list.opportunities, ...payload.opportunities],
                    country: payload.country ?? list.country,
                };
            }),
        }),
    )
    .handleAction(
        actionCreators.updateListOpportunitiesAsync.failure,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: false,
            opportunityListUpdateError: payload,
        }),
    )
    .handleAction(
        actionCreators.deleteOpportunityListAsync.request,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListDeleting: true,
            opportunityListDeleteError: undefined,
        }),
    )
    .handleAction(
        actionCreators.deleteOpportunityListAsync.failure,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListDeleting: false,
            opportunityListDeleteError: payload,
        }),
    )
    .handleAction(
        actionCreators.deleteOpportunityListAsync.success,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListDeleting: false,
        }),
    )
    .handleAction(
        actionCreators.removeOpportunityListAction,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityLists: removeFromOpportunityLists(state.opportunityLists, payload),
        }),
    )
    .handleAction(
        actionCreators.toggleOpportunityListModal,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListModal: payload,
        }),
    )
    .handleAction(
        actionCreators.fetchExcludeListsDuplicatesAsync.request,
        (state): OpportunitiesState => ({
            ...state,
            excludeListsDuplicateOpportunitiesFetching: true,
            opportunityListUpdateError: undefined,
        }),
    )
    .handleAction(
        actionCreators.fetchExcludeListsDuplicatesAsync.success,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            excludeListsDuplicateOpportunitiesFetching: false,
            excludeListsDuplicateOpportunities: payload,
        }),
    )
    .handleAction(
        actionCreators.fetchExcludeListsDuplicatesAsync.failure,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            excludeListsDuplicateOpportunitiesFetching: false,
            excludeListsDuplicateOpportunitiesError: payload,
        }),
    )
    .handleAction(
        actionCreators.toggleExcludeListsDuplicatesModal,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            isExcludeListsDuplicateOpportunitiesModalOpen: payload,
        }),
    )
    .handleAction(
        actionCreators.searchForWebsites.request,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListModal: {
                ...state.opportunityListModal,
                searching: true,
            },
        }),
    )
    .handleAction(
        actionCreators.searchForWebsites.failure,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListModal: {
                ...state.opportunityListModal,
                searching: false,
                searchError: payload,
            },
        }),
    )
    .handleAction(
        actionCreators.searchForWebsites.success,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListModal: {
                ...state.opportunityListModal,
                searching: false,
                searchResults: payload,
            },
        }),
    )
    .handleAction(
        actionCreators.tableRowsSelectionToggledAction,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            isRowSelectionEnabled: payload,
        }),
    )
    .handleAction(
        actionCreators.removeOpportunitiesFromList.request,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: true,
        }),
    )
    .handleAction(
        actionCreators.removeOpportunitiesFromList.success,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: false,
            opportunityLists: state.opportunityLists.map((list) => {
                return removeWebsitesFromListOpportunities(list, payload);
            }),
        }),
    )
    .handleAction(
        actionCreators.removeOpportunitiesFromList.failure,
        (state): OpportunitiesState => ({
            ...state,
            opportunityListUpdating: false,
        }),
    )
    .handleAction(
        actionCreators.downloadListTableExcelAsync.request,
        (state): OpportunitiesState => ({
            ...state,
            tableExcelDownloading: true,
            tableExcelDownloadError: INITIAL_OPPORTUNITIES_STATE.tableExcelDownloadError,
        }),
    )
    .handleAction(
        actionCreators.downloadListTableExcelAsync.success,
        (state): OpportunitiesState => ({
            ...state,
            tableExcelDownloading: false,
        }),
    )
    .handleAction(
        actionCreators.downloadListTableExcelAsync.failure,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            tableExcelDownloading: false,
            tableExcelDownloadError: payload,
        }),
    )
    .handleAction(
        actionCreators.fetchListRecommendationsAsync.request,
        (state): OpportunitiesState => ({
            ...state,
            recommendationsFetching: true,
        }),
    )
    .handleAction(
        actionCreators.fetchListRecommendationsAsync.success,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            recommendations: payload,
            recommendationsFetching: false,
        }),
    )
    .handleAction(
        actionCreators.fetchListRecommendationsAsync.failure,
        (state): OpportunitiesState => ({
            ...state,
            recommendationsFetching: false,
        }),
    )
    .handleAction(
        actionCreators.toggleRecommendationsBarAction,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            recommendationsBarOpen: payload,
        }),
    )
    .handleAction(
        actionCreators.setSelectedOpportunityListNameAndIdAction,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            opportunityListName: payload.opportunityListName,
            opportunityListId: payload.opportunityListId,
        }),
    )
    .handleAction(
        actionCreators.dismissRecommendationAsync.request,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            recommendations: state.recommendations.filter((recommendation) => recommendation.Domain !== payload),
        }),
    )
    .handleAction(
        actionCreators.removeRecommendedDomains,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            recommendations: state.recommendations.filter((recommendation) => !payload.includes(recommendation.Domain)),
        }),
    )
    .handleAction(
        actionCreators.notRelevantResultsModalPayloadSetAction,
        (state, { payload }): OpportunitiesState => ({
            ...state,
            notRelevantResultsModalPayload: payload,
        }),
    )
    .handleAction(actionCreators.setOpportunityListIdToShowUpdatedListModal, (state, { payload }) => ({
        ...state,
        opportunityListIdToShowUpdatedListModal: payload,
    }))
    .handleAction(actionCreators.setShouldWaitToFiltersFromSavedSearch, (state, { payload }) => ({
        ...state,
        shouldWaitToFiltersFromSavedSearch: payload,
    }));

export default opportunitiesReducer;
